import { Component, effect, inject, OnInit } from '@angular/core';
import { HeaderComponent } from '../../common-components/header/header.component';
import { TypeViewComponent } from './components/type-view/type-view.component';
import { FrequencyViewComponent } from './components/frequency-view/frequency-view.component';
import { NavigationBoxComponent } from '../../common-components/navigation-box/navigation-box.component';
import { NgIf } from '@angular/common';
import { RegionViewComponent } from './components/region-view/region-view.component';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, EMPTY, tap } from 'rxjs';
import { AssignmentService } from '../../services/assignment.service';
import { AdhocAuditRequest } from '../../models/adhoc-audit-request.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PopupComponent } from '../../common-components/popups/popup/popup.component';
import { ConfirmationComponent } from '../../common-components/popups/confirmation/confirmation.component';
import { ConfirmationModel } from '../../models/confirmation.model';
import { ConfirmationButton } from '../../models/confirmation-button.model';
import { ConfirmationButtonType } from '../../common-components/popups/confirmation/confirmation-button-type.enum';

@Component({
  selector: 'app-audit-setup-page',
  standalone: true,
  imports: [
    HeaderComponent,
    TypeViewComponent,
    FrequencyViewComponent,
    NavigationBoxComponent,
    NgIf,
    RegionViewComponent,
    TranslocoDirective,
    PopupComponent
  ],
  templateUrl: './audit-setup-page.component.html',
  styleUrl: './audit-setup-page.component.scss'
})
export class AuditSetupPageComponent implements OnInit {
  protected currentIndex = 0;
  protected maxIndex = 2;
  protected areaFormGroup = new FormGroup({ id: new FormControl<string>(''), name: new FormControl('') });
  protected locationFormGroup = new FormGroup({ id: new FormControl<string>(''), name: new FormControl('') });

  protected showErrorMessage: boolean = false;

  protected state = new AdhocAuditRequest();
  protected isOpen = false;
  protected popupData: any;
  protected readonly ConfirmationComponent = ConfirmationComponent;

  private router = inject(Router);
  private assignmentService = inject(AssignmentService);
  private translocoService = inject(TranslocoService);

  constructor() {
    effect(() => {
      const auditInProgress = this.assignmentService.auditInProgress();
      if (auditInProgress.length) {
        this.router.navigate(['audit', auditInProgress[0].layoutId]);
      }
    });
  }

  ngOnInit() {
    this.areaFormGroup.valueChanges.subscribe(value =>
      this.state.areaId = value.id ?? '');
    this.locationFormGroup.valueChanges.subscribe(value =>
      this.state.locationId = value.id ?? '');
  }

  get progress(): number {
    return ((this.currentIndex) / this.maxIndex) * 100;
  }

  protected goBack(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  protected goForward(): void {
    if (this.isNextButtonDisabled()) {
      if (this.currentIndex == 2) {
        this.areaFormGroup.markAsTouched();
        this.locationFormGroup.markAsTouched();
        this.areaFormGroup.updateValueAndValidity();
        this.locationFormGroup.updateValueAndValidity();
      }
      this.showErrorMessage = true;
      return;
    }

    this.showErrorMessage = false;

    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (this.currentIndex == this.maxIndex) {
      this.assignmentService.getTaskLayoutIdForAdhoc(this.state).pipe(
        tap((taskLayoutId) => this.router.navigate(['audit', taskLayoutId])),
        catchError((err) => {
          this.openErrorPopup(err.error.error);
          return EMPTY;
        })
      ).subscribe();
    }
  }

  protected isNextButtonDisabled(): boolean {
    if (this.currentIndex == 0) {
      return this.state.selectedType === undefined;
    } else if (this.currentIndex == 1) {
      return !this.state.prevalence;
    } else if (this.currentIndex == 2) {
      return !this.state.areaId || !this.state.locationId;
    }
    return false;
  }

  protected isNextIsAvailable(): boolean {
    return this.currentIndex != 0;
  }

  protected buttonText(): string {
    if (this.currentIndex == this.maxIndex) {
      return 'AUDIT-SETUP-PAGE.BUTTON.START';
    }
    return 'COMMON.LABEL.NEXT';
  }

  private openErrorPopup(error: string) {
    this.isOpen = !this.isOpen;
    this.popupData = new ConfirmationModel({
      imagePath: 'assets/incomplete.svg',
      prompt: this.translocoService.translate(error),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.OKAY'),
          type: ConfirmationButtonType.Confirm
        })]
    });
  }
}
